<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import {
  getInvalidConfiguredIds,
  isValidDelayedAssignmentWait,
  usesAutomationBlockingLabel,
} from '../helpers/conversationAutomationSettings';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSwitch from 'dashboard/components-next/switch/Switch.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from './SectionLayout.vue';

const MIN_WAIT_MINUTES = 1;
const MAX_WAIT_MINUTES = 10080;

const store = useStore();
const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');
const inboxUiFlags = useMapGetter('inboxes/getUIFlags');
const labels = useMapGetter('labels/getLabels');
const teams = useMapGetter('teams/getTeams');
const teamUiFlags = useMapGetter('teams/getUIFlags');

const enabled = ref(false);
const waitMinutes = ref(5);
const routes = ref([]);
const legacyTeamId = ref('');
const labelIds = ref([]);
const isSaving = ref(false);
const initializedAccountId = ref(null);
let nextRouteKey = 0;

const settings = computed(() => currentAccount.value?.settings || {});
const inboxOptions = computed(() =>
  inboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);
const labelOptions = computed(() =>
  labels.value.map(label => ({ value: label.id, label: label.title }))
);
const teamOptions = computed(() =>
  teams.value.map(team => ({ value: team.id, label: team.name }))
);

const invalidLabelIds = computed(() => {
  return getInvalidConfiguredIds(labelIds.value, labels.value);
});

const waitIsInvalid = computed(
  () =>
    !isValidDelayedAssignmentWait(
      waitMinutes.value,
      MIN_WAIT_MINUTES,
      MAX_WAIT_MINUTES
    )
);

const routeInboxIsInvalid = route =>
  !inboxUiFlags.value.isFetching &&
  !!route.inboxId &&
  !inboxes.value.some(inbox => inbox.id === Number(route.inboxId));

const routeTeamIsInvalid = route =>
  !teamUiFlags.value.isFetching &&
  !!route.teamId &&
  !teams.value.some(team => team.id === Number(route.teamId));

const duplicateInboxIds = computed(() => {
  const selectedIds = routes.value
    .map(route => Number(route.inboxId))
    .filter(Boolean);
  return selectedIds.filter(
    (inboxId, index) => selectedIds.indexOf(inboxId) !== index
  );
});

const routesAreValid = computed(
  () =>
    routes.value.length > 0 &&
    duplicateInboxIds.value.length === 0 &&
    routes.value.every(
      route =>
        !!route.inboxId &&
        !!route.teamId &&
        !routeInboxIsInvalid(route) &&
        !routeTeamIsInvalid(route)
    )
);

const hasIncompleteRoutes = computed(() =>
  routes.value.some(route => !route.inboxId || !route.teamId)
);

const hasInvalidRoutes = computed(() =>
  routes.value.some(
    route => routeInboxIsInvalid(route) || routeTeamIsInvalid(route)
  )
);

const sharesBlockingLabel = computed(() =>
  usesAutomationBlockingLabel(settings.value, labelIds.value)
);

const canSave = computed(
  () =>
    !isSaving.value &&
    (!enabled.value ||
      (!waitIsInvalid.value &&
        routesAreValid.value &&
        invalidLabelIds.value.length === 0))
);

const createRoute = (inboxId = '', teamId = '') => {
  nextRouteKey += 1;
  return {
    key: nextRouteKey,
    inboxId,
    teamId,
  };
};

const addRoute = () => {
  routes.value.push(createRoute());
};

const removeRoute = routeKey => {
  routes.value = routes.value.filter(route => route.key !== routeKey);
};

const availableInboxOptions = currentRoute => {
  const selectedInboxIds = new Set(
    routes.value
      .filter(route => route.key !== currentRoute.key)
      .map(route => Number(route.inboxId))
      .filter(Boolean)
  );
  return inboxOptions.value.filter(
    option =>
      Number(option.value) === Number(currentRoute.inboxId) ||
      !selectedInboxIds.has(Number(option.value))
  );
};

const hydrateLegacyRoutes = () => {
  if (!legacyTeamId.value || routes.value.length || !inboxes.value.length) {
    return;
  }

  routes.value = inboxes.value.map(inbox =>
    createRoute(inbox.id, legacyTeamId.value)
  );
  legacyTeamId.value = '';
};

const initialize = account => {
  if (!account?.id) return;

  const accountSettings = account.settings || {};
  const hasConfiguredRoutes = Object.prototype.hasOwnProperty.call(
    accountSettings,
    'delayed_unassigned_assignment_routes'
  );
  enabled.value = !!accountSettings.delayed_unassigned_assignment_enabled;
  waitMinutes.value =
    Number(accountSettings.delayed_unassigned_assignment_wait_minutes) || 5;
  routes.value = (
    accountSettings.delayed_unassigned_assignment_routes || []
  ).map(route => createRoute(route.inbox_id || '', route.team_id || ''));
  legacyTeamId.value = hasConfiguredRoutes
    ? ''
    : accountSettings.delayed_unassigned_assignment_team_id || '';
  labelIds.value = [
    ...new Set(accountSettings.delayed_unassigned_assignment_label_ids || []),
  ];
  initializedAccountId.value = account.id;
  hydrateLegacyRoutes();
};

watch(
  currentAccount,
  account => {
    if (account?.id !== initializedAccountId.value) initialize(account);
  },
  { immediate: true }
);

watch(inboxes, hydrateLegacyRoutes);

watch(enabled, isEnabled => {
  if (isEnabled && !routes.value.length && !legacyTeamId.value) addRoute();
});

const save = async () => {
  if (!canSave.value) return;

  isSaving.value = true;
  try {
    await updateAccount(
      {
        delayed_unassigned_assignment_enabled: enabled.value,
        delayed_unassigned_assignment_wait_minutes: Number(waitMinutes.value),
        delayed_unassigned_assignment_team_id: null,
        delayed_unassigned_assignment_routes: routes.value
          .filter(route => route.inboxId && route.teamId)
          .map(route => ({
            inbox_id: Number(route.inboxId),
            team_id: Number(route.teamId),
          })),
        delayed_unassigned_assignment_label_ids: [
          ...new Set(labelIds.value.map(Number)),
        ],
      },
      { silent: true }
    );
    useAlert(
      t('GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.API.SUCCESS')
    );
  } catch {
    useAlert(
      t('GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.API.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  if (!inboxes.value.length) store.dispatch('inboxes/get');
  if (!labels.value.length) store.dispatch('labels/get');
  if (!teams.value.length) store.dispatch('teams/get');
});
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.NOTE')"
    with-border
  >
    <template #headerActions>
      <div class="flex justify-end">
        <NextSwitch v-model="enabled" />
      </div>
    </template>

    <div class="grid gap-4">
      <WithLabel
        name="delayed-unassigned-assignment-wait"
        :label="
          t('GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.WAIT_LABEL')
        "
      >
        <NextInput
          v-model="waitMinutes"
          type="number"
          min="1"
          max="10080"
          :disabled="!enabled"
          :message="
            waitIsInvalid
              ? t(
                  'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.WAIT_ERROR'
                )
              : t(
                  'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.WAIT_HELP'
                )
          "
          :message-type="waitIsInvalid ? 'error' : 'info'"
        />
      </WithLabel>

      <div class="grid gap-3">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h4 class="mb-1 text-sm font-medium text-n-slate-12">
              {{
                t(
                  'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.ROUTES_LABEL'
                )
              }}
            </h4>
            <p class="mb-0 text-sm text-n-slate-11">
              {{
                t(
                  'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.ROUTES_HELP'
                )
              }}
            </p>
          </div>
          <NextButton
            type="button"
            outline
            slate
            icon="i-lucide-plus"
            :disabled="!enabled"
            :label="
              t('GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.ADD_ROUTE')
            "
            @click="addRoute"
          />
        </div>

        <div
          v-for="route in routes"
          :key="route.key"
          class="grid items-end gap-3 p-4 border rounded-xl border-n-weak md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
        >
          <WithLabel
            :name="`delayed-assignment-inbox-${route.key}`"
            :label="
              t(
                'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.INBOX_LABEL'
              )
            "
          >
            <ComboBox
              v-model="route.inboxId"
              :options="availableInboxOptions(route)"
              :disabled="!enabled"
              :placeholder="
                t(
                  'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.INBOX_PLACEHOLDER'
                )
              "
              :search-placeholder="
                t(
                  'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.INBOX_SEARCH'
                )
              "
              :empty-state="
                t(
                  'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.INBOX_EMPTY'
                )
              "
              :display-label="
                routeInboxIsInvalid(route)
                  ? t(
                      'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.INVALID_INBOX',
                      { id: route.inboxId }
                    )
                  : ''
              "
              :has-error="routeInboxIsInvalid(route)"
            />
          </WithLabel>

          <WithLabel
            :name="`delayed-assignment-team-${route.key}`"
            :label="
              t(
                'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.TEAM_LABEL'
              )
            "
          >
            <ComboBox
              v-model="route.teamId"
              :options="teamOptions"
              :disabled="!enabled"
              :placeholder="
                t(
                  'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.TEAM_PLACEHOLDER'
                )
              "
              :search-placeholder="
                t(
                  'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.TEAM_SEARCH'
                )
              "
              :empty-state="
                t(
                  'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.TEAM_EMPTY'
                )
              "
              :display-label="
                routeTeamIsInvalid(route)
                  ? t(
                      'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.INVALID_TEAM',
                      { id: route.teamId }
                    )
                  : ''
              "
              :has-error="routeTeamIsInvalid(route)"
            />
          </WithLabel>

          <NextButton
            type="button"
            ghost
            ruby
            icon="i-lucide-trash-2"
            :disabled="!enabled"
            :title="
              t(
                'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.REMOVE_ROUTE'
              )
            "
            :aria-label="
              t(
                'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.REMOVE_ROUTE'
              )
            "
            @click="removeRoute(route.key)"
          />
        </div>

        <p
          v-if="enabled && !routes.length"
          class="p-3 mb-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
        >
          {{
            t(
              'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.ROUTES_REQUIRED'
            )
          }}
        </p>
        <p
          v-if="enabled && hasIncompleteRoutes"
          class="p-3 mb-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
        >
          {{
            t(
              'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.ROUTE_INCOMPLETE'
            )
          }}
        </p>
        <p
          v-if="enabled && duplicateInboxIds.length"
          class="p-3 mb-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
        >
          {{
            t(
              'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.DUPLICATE_INBOX'
            )
          }}
        </p>
        <p
          v-if="hasInvalidRoutes"
          class="p-3 mb-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
        >
          {{
            t(
              'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.INVALID_ROUTES_WARNING'
            )
          }}
        </p>
      </div>

      <WithLabel
        name="delayed-unassigned-assignment-labels"
        :label="
          t('GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.LABELS_LABEL')
        "
      >
        <TagMultiSelectComboBox
          v-model="labelIds"
          :options="labelOptions"
          :disabled="!enabled"
          :placeholder="
            t(
              'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.LABELS_PLACEHOLDER'
            )
          "
          :search-placeholder="
            t(
              'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.LABELS_SEARCH'
            )
          "
          :empty-state="
            t(
              'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.LABELS_EMPTY'
            )
          "
          :message="
            t('GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.LABELS_HELP')
          "
          :has-error="invalidLabelIds.length > 0"
        />
      </WithLabel>

      <p
        v-if="invalidLabelIds.length"
        class="p-3 mb-0 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        {{
          t(
            'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.INVALID_LABELS_WARNING',
            { ids: invalidLabelIds.join(', ') }
          )
        }}
      </p>
      <p
        v-if="sharesBlockingLabel"
        class="p-3 mb-0 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        {{
          t(
            'GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.SHARED_BLOCKING_LABEL_WARNING'
          )
        }}
      </p>

      <div class="flex justify-end">
        <NextButton
          type="button"
          :disabled="!canSave"
          :is-loading="isSaving"
          :label="t('GENERAL_SETTINGS.FORM.DELAYED_UNASSIGNED_ASSIGNMENT.SAVE')"
          @click="save"
        />
      </div>
    </div>
  </SectionLayout>
</template>
